// ═══════════════════════════════════════
// Estado de empresa (ciclo comercial) — abril 2026
// ═══════════════════════════════════════
// Cambiar empresa.estado dispara efectos en los usuarios del cliente:
//   - suspension → rolAnterior = rolId actual; rolId = rol 'Suspendido'
//   - baja / baja_sin_cobro → activo = false (no pueden loguear)
//   - activo / asignado / stand_by → restaurar rolAnterior si venía de suspensión; activo=true
//     (nunca un rol de staff: ver el comentario del paso 2)
// La empresa y sus usuarios se escriben en UN batch: o cambia todo o nada.
//
// Uso:
//   import { cambiarEstadoEmpresa } from '@/lib/estadoEmpresa'
//   await cambiarEstadoEmpresa(empresaId, 'suspension', { motivo, actorUserId })
// ═══════════════════════════════════════

import {
  doc, collection, getDocs, query, where, writeBatch,
  serverTimestamp, runTransaction, getDoc,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import {
  COLLECTIONS, EMPRESA_ESTADOS, ROL_SUSPENDIDO, ROL_SUSPENDIDO_CODIGO, USER_TIPOS,
} from './constants'
import { esRolLadoMilbrands, ROLES_STAFF_MILBRANDS } from './ladoRol'

/**
 * Cambia el estado de una empresa y propaga el efecto a sus usuarios (cliente + colaborador).
 *
 * @param {string} empresaId
 * @param {string} nuevoEstado  - uno de EMPRESA_ESTADOS
 * @param {Object} opts
 * @param {string} [opts.motivo]        - texto libre para el log
 * @param {string} [opts.actorUserId]   - quién hizo el cambio
 * @returns {Promise<{usuariosAfectados: number, estadoAnterior: string}>}
 */
export async function cambiarEstadoEmpresa(empresaId, nuevoEstado, opts = {}) {
  const { motivo = '', actorUserId = null } = opts

  const empRef = doc(db, COLLECTIONS.EMPRESAS, empresaId)
  const empSnap = await getDoc(empRef)
  if (!empSnap.exists()) throw new Error(`Empresa ${empresaId} no existe`)

  const empData = empSnap.data()
  const estadoAnterior = empData.estado || EMPRESA_ESTADOS.ACTIVO

  if (estadoAnterior === nuevoEstado) {
    return { usuariosAfectados: 0, estadoAnterior }
  }

  // 1. Buscar usuarios de la empresa (cliente + colaborador con esta empresaId en empresaIds)
  const usersSnap = await getDocs(query(
    collection(db, COLLECTIONS.USUARIOS),
    where('empresaIds', 'array-contains', empresaId),
  ))

  // Filtrar solo los que son cliente o colaborador (no tocamos usuarios milbrands)
  const usuariosCliente = usersSnap.docs.filter(d => {
    const t = d.data().tipo
    return t === USER_TIPOS.CLIENTE || t === USER_TIPOS.COLABORADOR
  })

  // 2. Aplicar efecto según estado. La empresa va en el MISMO batch que sus
  // usuarios (antes se escribía sola y primero): si las reglas rechazan a un
  // usuario —por ejemplo, una cuenta protegida (ADR 2026-09-29)—, no queda la
  // empresa cambiada con los usuarios a medias.
  const batch = writeBatch(db)
  batch.update(empRef, {
    estado: nuevoEstado,
    estadoCambiadoEn: serverTimestamp(),
    estadoCambiadoPor: actorUserId,
  })
  let afectados = 0

  const rolSuspendido = await resolverRolSuspendidoId()

  // Al reactivar, cada rol a restaurar se lee UNA vez. «Es de staff» se mira POR
  // INCLUSIÓN (ADR 2026-09-29, D3): lo es si el catálogo lo declara del lado
  // Milbrands (`roles/{id}.lado`, `ladoRol.js`) O si está en la lista de staff
  // que usan las reglas (`isConsultor()`), aunque el catálogo diga cliente: con
  // ese `rolId`, las reglas lo tratarían como staff.
  const reactiva = ![EMPRESA_ESTADOS.SUSPENSION, EMPRESA_ESTADOS.BAJA, EMPRESA_ESTADOS.BAJA_SIN_COBRO].includes(nuevoEstado)
  const rolesAnteriores = reactiva
    ? [...new Set(usuariosCliente.map((d) => d.data().rolAnterior).filter(Boolean))]
    : []
  const docDeRol = new Map(await Promise.all(rolesAnteriores.map(async (rolId) => {
    const snap = await getDoc(doc(db, COLLECTIONS.ROLES, rolId))
    return [rolId, snap.exists() ? docConId(snap) : null]
  })))

  for (const uDoc of usuariosCliente) {
    const u = uDoc.data()
    const uRef = uDoc.ref
    const updates = { estadoEmpresaPropagadoEn: serverTimestamp() }

    if (nuevoEstado === EMPRESA_ESTADOS.SUSPENSION) {
      // Guardar rolAnterior SOLO si no estaba ya suspendido
      if (u.rolId !== rolSuspendido) {
        updates.rolAnterior = u.rolId || null
        updates.rolId = rolSuspendido
      }
      updates.activo = u.activo !== false // no lo bloqueamos, solo cambio de rol
    } else if (nuevoEstado === EMPRESA_ESTADOS.BAJA || nuevoEstado === EMPRESA_ESTADOS.BAJA_SIN_COBRO) {
      updates.activo = false
      // Mantenemos rolId como estaba — cuando se reactive se usa
    } else {
      // activo / asignado / stand_by → restaurar si venía de suspensión / baja.
      // Nunca un rol de staff: `rolAnterior` puede venir plantado por el propio
      // cliente (la rama self de las reglas no lo bloqueaba antes de la
      // contención del gate de identidad), y copiarlo a `rolId` lo volvía staff
      // —o Wizard— con la firma de quien reactiva (wpT730o6P259ZCQoV4QH, ADR
      // 2026-09-29). Queda en el rol Suspendido.
      const esRolDeStaff = ROLES_STAFF_MILBRANDS.includes(u.rolAnterior)
        || esRolLadoMilbrands(u.rolAnterior, docDeRol.get(u.rolAnterior))
      if (u.rolAnterior && !esRolDeStaff) {
        updates.rolId = u.rolAnterior
        updates.rolAnterior = null
      }
      updates.activo = true
    }

    batch.update(uRef, updates)
    afectados++
  }

  // 3. Log en auditoría
  batch.set(
    doc(collection(db, COLLECTIONS.AUDITORIA_EMPRESA_ESTADO)),
    {
      empresaId,
      estadoAnterior,
      estadoNuevo: nuevoEstado,
      usuariosAfectados: afectados,
      motivo,
      actorUserId,
      creadoEn: serverTimestamp(),
    }
  )

  await batch.commit()
  return { usuariosAfectados: afectados, estadoAnterior }
}

/**
 * Busca el rol "Suspendido" por código y retorna su id; si falta, lo crea.
 * Se busca por código y no por id porque puede haber uno de antes con id al azar
 * (report S0PfTXHuHFEVKHlu3Nn6). El que se crea lleva id fijo y los valores de
 * `ROL_SUSPENDIDO`, los únicos que las reglas le aceptan al staff. Va en
 * transacción: si dos cambios de estado lo crean a la vez, el segundo lo
 * encuentra en vez de pisarlo (pisarlo es un update, y eso es sólo del Wizard).
 */
async function resolverRolSuspendidoId() {
  const q = query(
    collection(db, COLLECTIONS.ROLES),
    where('codigo', '==', ROL_SUSPENDIDO_CODIGO),
  )
  const snap = await getDocs(q)
  if (!snap.empty) return snap.docs[0].id

  const ref = doc(db, COLLECTIONS.ROLES, ROL_SUSPENDIDO_CODIGO)
  await runTransaction(db, async (tx) => {
    if (!(await tx.get(ref)).exists()) tx.set(ref, { ...ROL_SUSPENDIDO, creadoEn: serverTimestamp() })
  })
  return ref.id
}

/**
 * Determina si un usuario está "bloqueado" por estado de empresa (para UI y login).
 * Devuelve { bloqueado, motivo } donde motivo es texto listo para mostrar.
 */
export async function estadoBloqueoPorEmpresa(userDoc) {
  if (!userDoc) return { bloqueado: false }
  const tipo = userDoc.tipo
  if (tipo !== USER_TIPOS.CLIENTE && tipo !== USER_TIPOS.COLABORADOR) {
    return { bloqueado: false }
  }
  const empresaIds = Array.isArray(userDoc.empresaIds) ? userDoc.empresaIds : []
  if (empresaIds.length === 0) return { bloqueado: false }

  // Traer estados de todas sus empresas
  const estados = []
  for (const eid of empresaIds) {
    const s = await getDoc(doc(db, COLLECTIONS.EMPRESAS, eid))
    if (s.exists()) estados.push({ id: eid, estado: s.data().estado || EMPRESA_ESTADOS.ACTIVO, nombre: s.data().nombre })
  }

  // Regla: si TODAS sus empresas están en baja → bloqueo total
  const todasEnBaja = estados.every(e => e.estado === EMPRESA_ESTADOS.BAJA || e.estado === EMPRESA_ESTADOS.BAJA_SIN_COBRO)
  if (todasEnBaja && estados.length > 0) {
    return {
      bloqueado: true,
      motivo: 'Tu cuenta fue dada de baja. Contactá a MilBrands para más información.',
      nivel: 'baja',
    }
  }

  // Si alguna está en suspensión y el resto no está activa → banner
  const algunaSuspendida = estados.some(e => e.estado === EMPRESA_ESTADOS.SUSPENSION)
  if (algunaSuspendida) {
    const empSuspendidas = estados.filter(e => e.estado === EMPRESA_ESTADOS.SUSPENSION).map(e => e.nombre).join(', ')
    return {
      bloqueado: false, // puede entrar, pero con solo ver
      motivo: `Servicio suspendido en: ${empSuspendidas}. Podés ver pero no realizar acciones.`,
      nivel: 'suspension',
    }
  }

  return { bloqueado: false }
}
